import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  HourglassEmpty,
  SendAndArchive,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'store/hook';
import useAppTheme from 'hooks/useAppTheme';
import { selectAssociatedAssets } from 'store/asset.slice';
import useCustomerInfo from 'hooks/useCustomerInfo';
import EventSourcePolyfillModule from 'event-source-polyfill';
import { addAcknowledgedAsset } from 'store/fleet-planning.slice';

type EventSourcePolyfillType = new (
  url: string,
  init?: { headers?: Record<string, string> }
) => EventSource;

const EventSourcePolyfill = (EventSourcePolyfillModule as any)
  .EventSourcePolyfill as EventSourcePolyfillType;

export const DeliveryStatusStep = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();

  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const { customerCode } = useCustomerInfo();
  const storeAssets = useAppSelector(selectAssociatedAssets);
  const acknowledgedAssetsList = useAppSelector(
    (s) => s.fleetPlanningReducer.acknowledgedAssets
  );
  const acknowledgedAssets = useMemo(
    () => new Set(acknowledgedAssetsList),
    [acknowledgedAssetsList]
  );

  // deviceId (e.g. symbotE401029) → bumperNumber (asset.name used in plan data)
  const deviceToBumper = useMemo(() => {
    const map = new Map<string, string>();
    storeAssets.forEach((a) => {
      if (a.device?.deviceId && a.bumperNumber) {
        map.set(a.device.deviceId, a.bumperNumber);
      }
    });
    return map;
  }, [storeAssets]);

  useEffect(() => {
    if (!accessToken || !customerId) return;
    const base = process.env.REACT_APP_MANAGEMENT_API_URL ?? '';
    const es = new EventSourcePolyfill(
      `${base}/api/dispatch-monitor/live-dispatch-sse?customerId=${customerId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    es.onmessage = (e: MessageEvent) => {
      try {
        const outer = JSON.parse(e.data as string) as {
          channel: string;
          message: string | Record<string, any>;
        };
        if (outer.channel !== `xtab:${customerCode}:last:pubsub`) return;
        console.log('Received SSE message:', outer);
        const msg =
          typeof outer.message === 'string'
            ? JSON.parse(outer.message)
            : outer.message;
        if (msg?.action !== 'PLAN_RECEIVED') return;
        const deviceId = msg?.device ?? msg?.deviceId;
        if (!deviceId) return;
        const bumper = deviceToBumper.get(String(deviceId));
        if (bumper) {
          dispatch(addAcknowledgedAsset(bumper));
        }
      } catch (_) {}
    };
    es.onerror = () => {
      /* noop */
    };
    return () => {
      es.close();
    };
  }, [accessToken, customerId, customerCode, deviceToBumper]);

  const assetLevelInput = useAppSelector(
    (state) => state.fleetPlanningReducer.assetLevelInput
  );
  const selectedPlanData = useAppSelector((state) =>
    state.fleetPlanningReducer.selectedPlan === 'user'
      ? state.fleetPlanningReducer.userPlan
      : state.fleetPlanningReducer.optimizedPlan
  );

  const planDispatched = useMemo(
    () =>
      Array.isArray(selectedPlanData.planData) &&
      selectedPlanData.planData.length > 0,
    [selectedPlanData.planData]
  );

  const dispatchedBumperNumbers = useMemo(() => {
    if (!planDispatched) return new Set<string>();
    return new Set(selectedPlanData.planData.map((d) => d.bumper_number));
  }, [planDispatched, selectedPlanData.planData]);

  const rows = useMemo(
    () =>
      assetLevelInput.map((asset) => ({
        id: asset.id,
        name: asset.name,
        dispatched: planDispatched && dispatchedBumperNumbers.has(asset.name),
        received: planDispatched && acknowledgedAssets.has(asset.name),
      })),
    [
      assetLevelInput,
      planDispatched,
      dispatchedBumperNumbers,
      acknowledgedAssets,
    ]
  );

  const totalReceived = rows.filter((r) => r.received).length;

  return (
    <Box display="flex" flexDirection="column" gap={2} height="100%">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <SendAndArchive sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography
            sx={{ fontWeight: 600, fontSize: '16px', color: 'text.primary' }}
          >
            {t('cdp_delivery_status_title')}
          </Typography>
        </Box>
        <Chip
          label={t('cdp_delivery_received_chip', {
            dispatched: totalReceived,
            total: rows.length,
          })}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: '12px',
            bgcolor:
              totalReceived === rows.length && rows.length > 0
                ? 'rgba(34,197,94,0.15)'
                : 'rgba(234,179,8,0.15)',
            color:
              totalReceived === rows.length && rows.length > 0
                ? '#16a34a'
                : '#ca8a04',
          }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${enableDarkTheme ? '#334155' : '#BDBDBD'}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table
            sx={{
              '& td, & th': {
                borderBottom: enableDarkTheme
                  ? '1px solid #2F445D'
                  : '1px solid #E0E0E0',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {t('cdp_table_header_asset')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 700, color: 'text.secondary' }}
                >
                  {t('cdp_table_header_status')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{ py: 6, color: 'text.disabled' }}
                  >
                    {t('cdp_no_assets_in_plan')}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          color: 'text.primary',
                        }}
                      >
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {row.received ? (
                        <Chip
                          icon={
                            <CheckCircle sx={{ fontSize: '16px !important' }} />
                          }
                          label={t('cdp_status_received')}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: 'rgba(34,197,94,0.15)',
                            color: '#16a34a',
                            '& .MuiChip-icon': { color: '#16a34a' },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={
                            <HourglassEmpty
                              sx={{ fontSize: '16px !important' }}
                            />
                          }
                          label={
                            row.dispatched
                              ? t('cdp_status_pending')
                              : t('cdp_status_not_sent')
                          }
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: row.dispatched
                              ? 'rgba(234,179,8,0.15)'
                              : 'rgba(148,163,184,0.15)',
                            color: row.dispatched ? '#ca8a04' : '#64748b',
                            '& .MuiChip-icon': {
                              color: row.dispatched ? '#ca8a04' : '#64748b',
                            },
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
